import React, { useEffect, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { format } from "date-fns";
import dayjs from "dayjs";
import fetchData from "../../functions/fetchData";

import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import { getHeaders } from "../../utility/getHeader";
import {
    Card,
    Row,
    Col,
    Statistic,
    Table,
    Tag,
    Space,
    Typography,
    Button,
    Modal,
    Input,
    InputNumber,
    DatePicker,
    Select,
    Segmented,
    Tooltip,
    Popconfirm,
} from "antd";
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    FilterOutlined,
    FileTextOutlined,
    CalculatorOutlined,
    ReloadOutlined,
    UserOutlined,
    ShopOutlined,
} from "@ant-design/icons";

const { Text } = Typography;
const { Option, OptGroup } = Select;

export default function SupplierLedgerEntry() {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [supplierBalance, setSupplierBalance] = useState(0);
    const [customerBalance, setCustomerBalance] = useState(0);
    const [editingItem, setEditingItem] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        transaction_id: "",
        date: format(new Date(), "yyyy-MM-dd"),
        account_type: "Account Payable",
        supplier_id: "",
        customer_id: "",
        description: "",
        amount: "",
        entry_type: "credit", // 'credit' for supplier payments, 'debit' for customer receipts
        reference_id: "",
    });

    // Filters
    const [filters, setFilters] = useState({
        supplier_id: "",
        start_date: "",
        end_date: "",
        entry_type: ""
    });

    useEffect(() => {
        const fetchSuppliers = async () => {
            try {
                fetchData("suppliers", setSuppliers, "id", {});
            } catch (err) {
                console.error("Error fetching suppliers:", err);
                toast.error("Failed to load suppliers.");
                setSuppliers([]);
            }
        };

        const fetchCustomers = async () => {
            try {
                fetchData("customers", setCustomers, "id", {});
            } catch (err) {
                console.error("Error fetching customers:", err);
                toast.error("Failed to load customers.");
                setCustomers([]);
            }
        };

        fetchSuppliers();
        fetchCustomers();
        fetchLedgerEntries();
    }, []);

    const fetchLedgerEntries = async () => {
        try {
            // Fetch supplier ledger entries
            const supplierEntries = await new Promise((resolve) => {
                const tempData = [];
                fetchData("supplier_ledger_entries", (data) => {
                    data.forEach(entry => {
                        tempData.push({
                            ...entry,
                            source_table: 'supplier_ledger_entries',
                            entry_category: entry.credit_amount > 0 ? 'credit' : 'debit'
                        });
                    });
                    resolve(tempData);
                }, "id", {});
            });

            // Fetch general ledger entries
            const generalEntries = await new Promise((resolve) => {
                const tempData = [];
                fetchData("ledger_entries", (data) => {
                    data.forEach(entry => {
                        // Only include customer-related entries (Account Receivable)
                        if (entry.account_type === 'Account Recievable') {
                            tempData.push({
                                ...entry,
                                source_table: 'ledger_entries',
                                entry_category: entry.debit_amount > 0 ? 'debit' : 'credit',
                                customer_id: entry.account_id
                            });
                        }
                    });
                    resolve(tempData);
                }, "id", {});
            });

            // Combine both datasets
            const combinedData = [...supplierEntries, ...generalEntries];

            // Sort by date (newest first)
            combinedData.sort((a, b) => new Date(b.date) - new Date(a.date));

            setData(combinedData);
            setFilteredData(combinedData);
        } catch (error) {
            console.error("Error fetching ledger entries:", error);
            setData([]);
            setFilteredData([]);
            toast.error("Failed to fetch ledger entries. Please check your connection.");
        }
    };

    const calculateSupplierBalance = async (supplierId) => {
        try {
            const response = await axios.get(`/supplier-ledger-balance/${supplierId}`, getHeaders());
            if (response.data.success) {
                setSupplierBalance(response.data.balance || 0);
            } else {
                setSupplierBalance(0);
            }
        } catch (error) {
            console.error("Error calculating balance:", error.response?.data || error.message);
            setSupplierBalance(0);
        }
    };

    const calculateCustomerBalance = async (customerId) => {
        try {
            const response = await axios.get(`/customer-ledger-balance/${customerId}`, getHeaders());
            if (response.data.success) {
                setCustomerBalance(response.data.balance || 0);
            } else {
                setCustomerBalance(0);
            }
        } catch (error) {
            console.error("Error calculating customer balance:", error.response?.data || error.message);
            setCustomerBalance(0);
        }
    };

    const handleSupplierChange = (supplierId) => {
        setFormData(prev => ({ ...prev, supplier_id: supplierId }));
        if (supplierId) {
            const supplier = suppliers.find(s => s.id === parseInt(supplierId));
            setSelectedSupplier(supplier);
            calculateSupplierBalance(supplierId);
        } else {
            setSelectedSupplier(null);
            setSupplierBalance(0);
        }
    };

    const handleCustomerChange = (customerId) => {
        setFormData(prev => ({ ...prev, customer_id: customerId }));
        if (customerId) {
            const customer = customers.find(c => c.id === parseInt(customerId));
            setSelectedCustomer(customer);
            calculateCustomerBalance(customerId);
        } else {
            setSelectedCustomer(null);
            setCustomerBalance(0);
        }
    };

    const handleEntryTypeChange = (value) => {
        setFormData(prev => ({
            ...prev,
            entry_type: value,
            supplier_id: "",
            customer_id: ""
        }));
        setSelectedSupplier(null);
        setSelectedCustomer(null);
        setSupplierBalance(0);
        setCustomerBalance(0);
    };

    const handleSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();

        const transactionId = formData.transaction_id || `TXN_${Date.now()}`;
        const amount = parseFloat(formData.amount);

        try {
            if (formData.entry_type === 'credit') {
                // Credit entry: Payment to supplier -> save to supplier_ledger_entries
                const supplierLedgerData = {
                    transaction_id: transactionId,
                    date: formData.date,
                    account_type: "Account Payable",
                    supplier_id: parseInt(formData.supplier_id),
                    description: formData.description,
                    debit_amount: 0.00,
                    credit_amount: amount, // Credit to reduce supplier balance
                    reference_id: formData.reference_id || null,
                };

                const response = await axios.post("/supplier-ledger-entry", supplierLedgerData, getHeaders());

                if (response.data.success) {
                    toast.success("Supplier payment recorded successfully!");
                } else {
                    throw new Error(response.data.message || "Failed to save supplier payment");
                }

            } else if (formData.entry_type === 'debit') {
                // Debit entry: Receipt from customer -> save to customer ledger
                const customerLedgerData = {
                    customer_id: parseInt(formData.customer_id),
                    description: formData.description,
                    debit_amount: amount, // Debit customer account
                    credit_amount: 0.00,
                    account_type: "Account Receivable",
                    reference_id: formData.reference_id || null,
                    date: formData.date,
                };

                const response = await axios.post("/customer-ledger-entry", customerLedgerData, getHeaders());

                if (response.data.success) {
                    toast.success("Customer receipt recorded successfully!");
                } else {
                    throw new Error(response.data.message || "Failed to save customer receipt");
                }
            }

            resetForm();
            setShowModal(false);
            fetchLedgerEntries();

            // Update balance based on entry type
            if (formData.entry_type === 'credit' && formData.supplier_id) {
                calculateSupplierBalance(formData.supplier_id);
            } else if (formData.entry_type === 'debit' && formData.customer_id) {
                calculateCustomerBalance(formData.customer_id);
            }

        } catch (error) {
            console.error(error);
            toast.error("Failed to save entry.");
        }
    };

    const resetForm = () => {
        setFormData({
            transaction_id: "",
            date: format(new Date(), "yyyy-MM-dd"),
            account_type: "Account Payable",
            supplier_id: "",
            customer_id: "",
            description: "",
            amount: "",
            entry_type: "credit",
            reference_id: "",
        });
        setEditingItem(null);
        setSelectedSupplier(null);
        setSelectedCustomer(null);
        setSupplierBalance(0);
        setCustomerBalance(0);
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setFormData({
            transaction_id: item.transaction_id,
            date: format(new Date(item.date), "yyyy-MM-dd"),
            account_type: item.account_type,
            supplier_id: item.supplier_id.toString(),
            customer_id: "",
            description: item.description,
            amount: item.debit_amount > 0 ? item.debit_amount : item.credit_amount,
            entry_type: item.debit_amount > 0 ? "debit" : "credit",
            reference_id: item.reference_id || "",
        });
        handleSupplierChange(item.supplier_id.toString());
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`/supplier-ledger-entry/${id}`, getHeaders());
            toast.success("Entry deleted successfully!");
            fetchLedgerEntries();
        } catch (error) {
            console.error(error);
            toast.error("Failed to delete entry.");
        }
    };

    const applyFilters = () => {
        let filtered = data;

        if (filters.supplier_id) {
            filtered = filtered.filter(item =>
                (item.source_table === 'supplier_ledger_entries' && item.supplier_id === parseInt(filters.supplier_id)) ||
                (item.source_table === 'ledger_entries' && item.customer_id === parseInt(filters.supplier_id))
            );
        }

        if (filters.start_date) {
            filtered = filtered.filter(item => new Date(item.date) >= new Date(filters.start_date));
        }

        if (filters.end_date) {
            filtered = filtered.filter(item => new Date(item.date) <= new Date(filters.end_date));
        }

        if (filters.entry_type) {
            filtered = filtered.filter(item => {
                if (filters.entry_type === 'debit') {
                    return item.entry_category === 'debit';
                } else if (filters.entry_type === 'credit') {
                    return item.entry_category === 'credit';
                }
                return true;
            });
        }

        setFilteredData(filtered);
    };

    const resetFilters = () => {
        setFilters({
            supplier_id: "",
            start_date: "",
            end_date: "",
            entry_type: ""
        });
        setFilteredData(data);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount) + ' THB';
    };

    const totalDebit = filteredData.reduce((sum, entry) => sum + parseFloat(entry.debit_amount || 0), 0);
    const totalCredit = filteredData.reduce((sum, entry) => sum + parseFloat(entry.credit_amount || 0), 0);

    const columns = [
        { title: 'Date', dataIndex: 'date', key: 'date', render: (v) => format(new Date(v), "MMM dd, yyyy"), sorter: (a, b) => new Date(a.date) - new Date(b.date) },
        { title: 'Transaction ID', dataIndex: 'transaction_id', key: 'transaction_id', render: (v) => <Tag>{v}</Tag> },
        {
            title: 'Supplier/Customer',
            key: 'party',
            render: (_, entry) => {
                const supplier = entry.source_table === 'supplier_ledger_entries'
                    ? suppliers.find(s => s.id === entry.supplier_id)
                    : null;
                const customer = entry.source_table === 'ledger_entries'
                    ? customers.find(c => c.id === entry.customer_id)
                    : null;
                return (
                    <div>
                        <Text strong>{supplier ? supplier.supplier_name : customer ? customer.name : 'Unknown'}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {entry.source_table === 'supplier_ledger_entries' ? 'Supplier' : 'Customer'}
                        </Text>
                    </div>
                );
            }
        },
        { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
        {
            title: 'Type',
            key: 'type',
            render: (_, entry) => (
                <Tag color={entry.entry_category === 'debit' ? 'blue' : 'green'}>
                    {entry.entry_category === 'debit'
                        ? (entry.source_table === 'supplier_ledger_entries' ? 'Supplier Expense' : 'Customer Receipt')
                        : 'Supplier Payment'}
                </Tag>
            )
        },
        {
            title: 'Debit',
            dataIndex: 'debit_amount',
            key: 'debit_amount',
            align: 'right',
            render: (v) => v > 0 ? <Text strong style={{ color: '#dc2626' }}>{formatCurrency(v)}</Text> : '-'
        },
        {
            title: 'Credit',
            dataIndex: 'credit_amount',
            key: 'credit_amount',
            align: 'right',
            render: (v) => v > 0 ? <Text strong style={{ color: '#16a34a' }}>{formatCurrency(v)}</Text> : '-'
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'center',
            render: (_, entry) => (
                entry.source_table === 'supplier_ledger_entries' ? (
                    <Space>
                        <Tooltip title="Edit Entry">
                            <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(entry)} />
                        </Tooltip>
                        <Popconfirm
                            title="Delete this entry?"
                            okText="Delete"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => handleDelete(entry.id)}
                        >
                            <Tooltip title="Delete Entry">
                                <Button size="small" danger icon={<DeleteOutlined />} />
                            </Tooltip>
                        </Popconfirm>
                    </Space>
                ) : (
                    <Tag>Read Only</Tag>
                )
            )
        },
    ];

    return (
        <Layout>
            <Header title="Supplier Ledger Entry" />
            <ToastContainer />

            <div style={{ padding: 20 }}>
                <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
                    <Col>
                        <Typography.Title level={4} style={{ margin: 0 }}>
                            <FileTextOutlined style={{ marginRight: 8, color: '#0d9488' }} />
                            Ledger Entries
                        </Typography.Title>
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => { resetForm(); setShowModal(true); }}
                        >
                            New Entry
                        </Button>
                    </Col>
                </Row>

                {/* Summary Cards */}
                <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                    <Col xs={12} md={6}>
                        <Card style={{ borderRadius: 14, background: '#fef2f2' }}>
                            <Statistic title="Total Debit" value={totalDebit} precision={2} suffix="THB" valueStyle={{ color: '#dc2626' }} />
                        </Card>
                    </Col>
                    <Col xs={12} md={6}>
                        <Card style={{ borderRadius: 14, background: '#f0fdf4' }}>
                            <Statistic title="Total Credit" value={totalCredit} precision={2} suffix="THB" valueStyle={{ color: '#16a34a' }} />
                        </Card>
                    </Col>
                    <Col xs={12} md={6}>
                        <Card style={{ borderRadius: 14, background: '#eff6ff' }}>
                            <Statistic title="Net Balance" value={totalDebit - totalCredit} precision={2} suffix="THB" valueStyle={{ color: '#2563eb' }} />
                        </Card>
                    </Col>
                    <Col xs={12} md={6}>
                        <Card style={{ borderRadius: 14, background: '#f0fdfa' }}>
                            <Statistic title="Total Records" value={filteredData.length} valueStyle={{ color: '#0d9488' }} />
                        </Card>
                    </Col>
                </Row>

                {/* Filters */}
                <Card
                    style={{ borderRadius: 14, marginBottom: 20 }}
                    title={<Space><FilterOutlined style={{ color: '#0d9488' }} /><Text strong>Filters & Reports</Text></Space>}
                >
                    <Row gutter={[16, 16]} align="bottom">
                        <Col xs={24} md={6}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Filter by Supplier/Customer</Text>
                            <Select
                                allowClear
                                showSearch
                                style={{ width: '100%' }}
                                placeholder="All Suppliers & Customers"
                                value={filters.supplier_id || undefined}
                                onChange={(value) => setFilters(prev => ({ ...prev, supplier_id: value || "" }))}
                                filterOption={(input, option) => option.children?.toLowerCase().includes(input.toLowerCase())}
                            >
                                <OptGroup label="Suppliers">
                                    {suppliers.map((supplier) => (
                                        <Option key={`sup_${supplier.id}`} value={String(supplier.id)}>{supplier.supplier_name}</Option>
                                    ))}
                                </OptGroup>
                                <OptGroup label="Customers">
                                    {customers.map((customer) => (
                                        <Option key={`cust_${customer.id}`} value={String(customer.id)}>{customer.name}</Option>
                                    ))}
                                </OptGroup>
                            </Select>
                        </Col>
                        <Col xs={24} md={5}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Start Date</Text>
                            <DatePicker
                                style={{ width: '100%' }}
                                format="DD-MM-YYYY"
                                value={filters.start_date ? dayjs(filters.start_date) : null}
                                onChange={(date) => setFilters(prev => ({ ...prev, start_date: date ? date.format('YYYY-MM-DD') : '' }))}
                            />
                        </Col>
                        <Col xs={24} md={5}>
                            <Text type="secondary" style={{ fontSize: 12 }}>End Date</Text>
                            <DatePicker
                                style={{ width: '100%' }}
                                format="DD-MM-YYYY"
                                value={filters.end_date ? dayjs(filters.end_date) : null}
                                onChange={(date) => setFilters(prev => ({ ...prev, end_date: date ? date.format('YYYY-MM-DD') : '' }))}
                            />
                        </Col>
                        <Col xs={24} md={4}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Entry Type</Text>
                            <Select
                                allowClear
                                style={{ width: '100%' }}
                                placeholder="All Types"
                                value={filters.entry_type || undefined}
                                onChange={(value) => setFilters(prev => ({ ...prev, entry_type: value || "" }))}
                            >
                                <Option value="debit">Expenses</Option>
                                <Option value="credit">Payments</Option>
                            </Select>
                        </Col>
                        <Col xs={24} md={4}>
                            <Space>
                                <Button type="primary" onClick={applyFilters}>Apply</Button>
                                <Button icon={<ReloadOutlined />} onClick={resetFilters}>Reset</Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>

                {/* Ledger Entries Table */}
                <Card style={{ borderRadius: 14 }} title={<Text strong>Ledger Entries ({filteredData.length} records)</Text>}>
                    <Table
                        rowKey={(entry) => `${entry.source_table}_${entry.id}`}
                        columns={columns}
                        dataSource={filteredData}
                        pagination={{ pageSize: 10, showSizeChanger: true }}
                        scroll={{ x: true }}
                        locale={{ emptyText: 'No ledger entries found.' }}
                    />
                </Card>
            </div>

            {/* New / Edit Entry Modal */}
            <Modal
                title={
                    <Space>
                        <PlusOutlined style={{ color: '#0d9488' }} />
                        {editingItem ? 'Edit Entry' : 'New Entry'}
                    </Space>
                }
                open={showModal}
                onCancel={() => { setShowModal(false); resetForm(); }}
                footer={null}
                width={680}
            >
                <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Entry Type <span style={{ color: '#ff4d4f' }}>*</span></Text>
                        <Segmented
                            block
                            value={formData.entry_type}
                            onChange={handleEntryTypeChange}
                            options={[
                                { label: 'Supplier Payment', value: 'credit', icon: <ShopOutlined /> },
                                { label: 'Customer Receipt', value: 'debit', icon: <UserOutlined /> },
                            ]}
                        />
                    </div>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <div className="mb-3">
                                <Text strong style={{ display: 'block', marginBottom: 6 }}>Date <span style={{ color: '#ff4d4f' }}>*</span></Text>
                                <DatePicker
                                    value={formData.date ? dayjs(formData.date) : null}
                                    onChange={(date) => setFormData(prev => ({ ...prev, date: date ? date.format('YYYY-MM-DD') : '' }))}
                                    style={{ width: '100%' }}
                                    format="DD-MM-YYYY"
                                    allowClear={false}
                                />
                            </div>
                        </Col>
                        <Col xs={24} md={12}>
                            <div className="mb-3">
                                <Text strong style={{ display: 'block', marginBottom: 6 }}>
                                    {formData.entry_type === 'credit' ? 'Supplier' : 'Customer'} <span style={{ color: '#ff4d4f' }}>*</span>
                                </Text>
                                {formData.entry_type === 'credit' ? (
                                    <Select
                                        value={formData.supplier_id || undefined}
                                        onChange={(value) => handleSupplierChange(value)}
                                        placeholder="Select Supplier"
                                        style={{ width: '100%' }}
                                        showSearch
                                        optionFilterProp="children"
                                    >
                                        {suppliers.map((supplier) => (
                                            <Option key={supplier.id} value={String(supplier.id)}>{supplier.supplier_name}</Option>
                                        ))}
                                    </Select>
                                ) : (
                                    <Select
                                        value={formData.customer_id || undefined}
                                        onChange={(value) => handleCustomerChange(value)}
                                        placeholder="Select Customer"
                                        style={{ width: '100%' }}
                                        showSearch
                                        optionFilterProp="children"
                                    >
                                        {customers.map((customer) => (
                                            <Option key={customer.id} value={String(customer.id)}>{customer.name}</Option>
                                        ))}
                                    </Select>
                                )}
                            </div>
                        </Col>
                    </Row>

                    {(selectedSupplier || selectedCustomer) && (
                        <Card size="small" style={{ background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 10, marginBottom: 16 }}>
                            <Space>
                                <CalculatorOutlined style={{ color: '#0d9488' }} />
                                <Text>
                                    {formData.entry_type === 'credit' ? 'Supplier' : 'Customer'} Balance:{' '}
                                    <Text strong style={{ color: '#0d9488' }}>
                                        {formatCurrency(formData.entry_type === 'credit' ? supplierBalance : customerBalance)}
                                    </Text>
                                </Text>
                            </Space>
                        </Card>
                    )}

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <div className="mb-3">
                                <Text strong style={{ display: 'block', marginBottom: 6 }}>Amount <span style={{ color: '#ff4d4f' }}>*</span></Text>
                                <InputNumber
                                    min={0}
                                    step={0.01}
                                    value={formData.amount}
                                    onChange={(value) => setFormData(prev => ({ ...prev, amount: value }))}
                                    style={{ width: '100%' }}
                                    placeholder="0.00"
                                />
                            </div>
                        </Col>
                        <Col xs={24} md={12}>
                            <div className="mb-3">
                                <Text strong style={{ display: 'block', marginBottom: 6 }}>Reference ID</Text>
                                <Input
                                    value={formData.reference_id}
                                    onChange={(e) => setFormData(prev => ({ ...prev, reference_id: e.target.value }))}
                                    placeholder="Optional reference"
                                />
                            </div>
                        </Col>
                    </Row>

                    <div className="mb-3">
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>Description <span style={{ color: '#ff4d4f' }}>*</span></Text>
                        <Input.TextArea
                            rows={2}
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Transaction description"
                        />
                    </div>

                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => { setShowModal(false); resetForm(); }}>Cancel</Button>
                            <Button type="primary" htmlType="submit">
                                {editingItem ? 'Update' : 'Save'} {formData.entry_type === 'credit' ? 'Payment' : 'Receipt'}
                            </Button>
                        </Space>
                    </div>
                </form>
            </Modal>
        </Layout>
    );
}
